import React from 'react';
import { Box, ButtonBase, Chip, Stack, Tooltip, Typography } from '@mui/material';
import CheckCircle from '@mui/icons-material/CheckCircle';
import CheckBoxOutlineBlank from '@mui/icons-material/CheckBoxOutlineBlank';
import type { DeviceMetric } from '../../../../services/deviceMetricTypes';
import { metricPresentation, recordingKind } from './metricPresentation';
import DeviceMetricIcon from './DeviceMetricIcon';
import MetricVerification from './MetricVerification';

interface Props {
  metric: DeviceMetric;
  active: boolean;
  multiple: boolean;
  selectionOnly: boolean;
  showAvailability: boolean;
  count: number;
  recordedDays: number;
  onChoose: (route: string) => void;
}

export default function DeviceMetricCard({
  metric,
  active,
  multiple,
  selectionOnly,
  showAvailability,
  count,
  recordedDays,
  onChoose,
}: Props) {
  const item = metricPresentation(metric);
  const choiceIconSize = selectionOnly ? 16 : 18;
  const choiceIcon = active ? (
    <CheckCircle sx={{ color: item.color, fontSize: choiceIconSize, flexShrink: 0 }} />
  ) : (selectionOnly || multiple) && (
    <CheckBoxOutlineBlank
      sx={{ color: selectionOnly ? 'text.disabled' : 'text.secondary', fontSize: choiceIconSize, flexShrink: 0 }}
    />
  );
  const focusStyle = { outline: `3px solid ${item.color}`, outlineOffset: 2 };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        border: '1px solid',
        borderRadius: selectionOnly ? 2 : 2.5,
        borderColor: active ? item.color : item.border,
        borderLeftWidth: 3,
        borderLeftColor: item.color,
        bgcolor: active ? item.selectedBackground : item.background,
        boxShadow: active ? `inset 0 0 0 1px ${item.color}` : 'none',
        '&:hover': { borderColor: item.color, bgcolor: item.selectedBackground },
      }}
    >
      {selectionOnly ? (
        <Tooltip title={`${item.categoryLabel}: ${item.description}`} describeChild>
          <ButtonBase
            onClick={() => onChoose(metric.routeKey)}
            aria-label={item.label}
            aria-pressed={active}
            sx={{
              p: 1,
              minHeight: 62,
              textAlign: 'left',
              justifyContent: 'flex-start',
              gap: 0.75,
              borderRadius: 'inherit',
              flex: 1,
              '&.Mui-focusVisible': focusStyle,
            }}
          >
            <DeviceMetricIcon metric={metric} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 650, lineHeight: 1.3 }}>
                {item.label}
              </Typography>
              <Typography
                component="p"
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: 10, mt: 0.25, lineHeight: 1.3 }}
              >
                {recordingKind(metric)}
              </Typography>
            </Box>
            {choiceIcon}
          </ButtonBase>
        </Tooltip>
      ) : (
        <ButtonBase
          onClick={() => onChoose(metric.routeKey)}
          aria-pressed={active}
          aria-label={showAvailability
            ? `${item.label}, ${count ? `${count.toLocaleString()} records` : 'no data in these dates'}`
            : item.label}
          sx={{
            textAlign: 'left',
            p: 1.75,
            borderRadius: 'inherit',
            flex: 1,
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
            '&.Mui-focusVisible': focusStyle,
          }}
        >
          <Box sx={{ width: '100%' }}>
            <Stack direction="row" justifyContent="space-between" gap={1} alignItems="center">
              <DeviceMetricIcon metric={metric} />
              <Typography fontWeight={650} color={active ? item.color : 'text.primary'}>
                {item.label}
              </Typography>
              {choiceIcon}
            </Stack>
            <Typography
              variant="body2"
              component="p"
              color="text.secondary"
              sx={{ mt: 0.5, mb: 1.25, minHeight: 40 }}
            >
              {item.description}
            </Typography>
            <Stack direction="row" gap={0.75} flexWrap="wrap" sx={{ mt: 0.75 }}>
              <Chip
                size="small"
                variant="outlined"
                label={recordingKind(metric)}
                sx={{ height: 24, fontSize: 11 }}
              />
              {metric.fields.length > 1 && (
                <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                  {metric.fields.length} measurement options
                </Typography>
              )}
            </Stack>
            {showAvailability && (
              <Stack direction="row" gap={0.75} alignItems="center" flexWrap="wrap">
                <Chip
                  size="small"
                  label={count ? `${count.toLocaleString()} records` : 'No data in these dates'}
                  sx={{
                    fontSize: 11,
                    height: 24,
                    bgcolor: count ? '#edf6f1' : '#f1f3f5',
                    color: count ? '#28624a' : 'text.secondary',
                  }}
                />
              </Stack>
            )}
            {multiple && showAvailability && (
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.75 }}>
                {recordedDays} recorded days in your selected dates
              </Typography>
            )}
          </Box>
        </ButtonBase>
      )}
      {item.unverified && (
        <Box sx={{ px: selectionOnly ? 0.75 : 1.75, pb: selectionOnly ? 0.5 : 1.5 }}>
          <MetricVerification metric={metric} compact={selectionOnly} />
        </Box>
      )}
    </Box>
  );
}
